import React from 'react';
import styled from 'styled-components';
import { BreakpointWidth, media, mobileLandscape } from '@peloton/styles';
import { PelotonLogo as PelotonLogoNew } from '@acme-ui/images/PelotonLogo';
import { gray4, white } from '@engage/colors/base';
import { spaces } from '@engage/styles/spaces';
import { useIsRemoteActivationFlowImprovementsEnabled } from '../../hooks/useIsRemoteActivationFlowImprovementsEnabled';
import Footer from '../footer/Footer';
import UpdatedFooter from '../footer/UpdatedFooter';
import { useError } from '../Oops';
import Oops from '../Oops/Oops';
import DotsBackground from './DotsBackground';
import { GradientBackground } from './GradientBackground';
import PelotonLogo from './PelotonLogo';

interface DarkLayoutProps {
  children: React.ReactNode;
  removeBottomMargin?: boolean;
}

const DarkLayout: React.FC<DarkLayoutProps> = ({ children, removeBottomMargin }) => {
  const { shouldShowOopsPage } = useError();
  const isRemoteActivationFlowImprovementsEnabled =
    useIsRemoteActivationFlowImprovementsEnabled();
  const [isActivationUI, setIsActivationUI] = React.useState<boolean>(false);

  React.useEffect(() => {
    const isActivation = window.location.pathname.endsWith('/activate');
    setIsActivationUI(isRemoteActivationFlowImprovementsEnabled && isActivation);
  }, [isRemoteActivationFlowImprovementsEnabled]);

  if (shouldShowOopsPage) {
    return <Oops />;
  }

  return (
    <Background newUI={isActivationUI}>
      <div className="container">
        <div className="logo-container">
          {isActivationUI ? <PelotonLogoNew /> : <StyledPelotonLogo />}
        </div>
        {children}
      </div>
      {isActivationUI ? <UpdatedFooter /> : <StyledFooter darkTheme />}
      <style jsx>{`
        .container {
          max-width: 620px;
          width: 100%;
          display: flex;
          align-self: center;
          flex-direction: column;
        }

        .logo-container {
          display: flex;
          align-self: center;
          margin-top: ${spaces.large}px;
          margin-bottom: 50px;
        }

        @media (min-width: ${BreakpointWidth.tablet}px) {
          .logo-container {
            margin-top: ${spaces.huge}px;
            margin-bottom: ${removeBottomMargin ? '50px' : '120px'};
          }
        }

        @media only screen and (min-width: 320px) and (max-width: 824px) and (orientation: landscape) {
          .logo-container {
            margin-top: ${spaces.large}px;
            margin-bottom: 50px;
          }
        }

        .centered-block {
          text-align: center;
          margin-top: ${spaces.large}px;
        }

        .sign-up-text {
          margin-top: 30px;
          font-size: 12px;
          font-weight: 400;
          color: ${gray4};
        }
      `}</style>
    </Background>
  );
};

const Background: React.FC<React.PropsWithChildren<{ newUI: boolean }>> = ({
  newUI,
  children,
}) =>
  newUI ? (
    <GradientBackground>{children}</GradientBackground>
  ) : (
    <DotsBackground>{children}</DotsBackground>
  );

const StyledPelotonLogo = styled(PelotonLogo)`
  height: 30px;
  width: 100px;
  fill: ${white};
  ${media.tablet`
    height: 40px;
    width: 135px;
  `};
  ${mobileLandscape`
    height: 30px;
    width: 100px;
  `}
`;

const StyledFooter = styled(Footer)`
  flex-shrink: 0;
`;

export default DarkLayout;
